import { useEffect, useRef } from 'react';
import type { RestaurantResponseData } from '@/lib/types.ts';
import { track } from '@/lib/analytics/index.ts';

interface WidgetAnalyticsParams {
  restaurant: RestaurantResponseData | null | undefined;
  /** Referral code from `?code=` (already sanitized). */
  code: string | null;
  /** Raw `?event_id=` deeplink value, if present. */
  urlEventIdRaw: string | null;
  /** Whether the booking surface is still resolving events/availability. */
  isLoading: boolean;
  /** True when at least one bookable event is available. */
  hasEvents: boolean;
  /** True when at least one bookable classic date is available. */
  hasClassicDates: boolean;
  /** Currently selected classic date, if any. */
  date?: string;
}

/**
 * Fires the top-of-funnel widget events — `Widget Loaded` and
 * `Widget Schedule Viewed` — exactly once per restaurant load.
 *
 * EventPage is reused across SPA navigation between restaurants without
 * remounting, so the ref guards are keyed on restaurant id rather than a
 * plain boolean. The effect dependency arrays are intentionally broad (e.g.
 * `date` changes on every date pick); the refs are the real once-per-restaurant
 * guard.
 */
export function useWidgetAnalytics({
  restaurant,
  code,
  urlEventIdRaw,
  isLoading,
  hasEvents,
  hasClassicDates,
  date,
}: WidgetAnalyticsParams): void {
  const widgetLoadedRef = useRef<number | null>(null);
  const scheduleViewedRef = useRef<number | null>(null);

  // `Widget Loaded` — once the restaurant resolves.
  useEffect(() => {
    if (restaurant?.id == null) return;
    const restaurantId = Number(restaurant.id);
    if (!Number.isFinite(restaurantId) || widgetLoadedRef.current === restaurantId) return;
    widgetLoadedRef.current = restaurantId;
    track('Widget Loaded', {
      restaurant_id: restaurantId,
      booking_type: restaurant.type === 'event' ? 'event' : 'restaurant',
      referral_code: code ?? undefined,
      event_id: urlEventIdRaw ? Number(urlEventIdRaw) || undefined : undefined,
    });
  }, [restaurant?.id, restaurant?.type, code, urlEventIdRaw]);

  // `Widget Schedule Viewed` — once availability has resolved (events or
  // classic dates), reporting whether anything is bookable.
  useEffect(() => {
    if (restaurant?.id == null || isLoading) return;
    const restaurantId = Number(restaurant.id);
    if (!Number.isFinite(restaurantId) || scheduleViewedRef.current === restaurantId) return;
    scheduleViewedRef.current = restaurantId;
    track('Widget Schedule Viewed', {
      restaurant_id: restaurantId,
      booking_type: restaurant.type === 'event' ? 'event' : 'restaurant',
      has_availability: hasEvents || hasClassicDates,
      date,
    });
  }, [restaurant?.id, restaurant?.type, isLoading, hasEvents, hasClassicDates, date]);
}
